import React from "react";
import dayjs from "dayjs";
import * as Yup from 'yup';
import { isNumber } from "lodash";
import { TrashIcon } from "./trash";
import { Container } from './styled';
import { useRouter } from "next/router";
import { LoadingBuffer } from "../utils/Loading";
import { Grid, Pagination } from '@mui/material';
import { yupResolver } from "@hookform/resolvers/yup";
import { DeleteElaborationDialog } from "../../Modal/Delete";
import { TextFieldElaboration } from "../../Inputs/TextField";
import { ActionButtonElaborations } from "../../ActionButton";
import { useTipeOfFlow } from "@/services/zustand/tipo_fluxo";
import { Organograma } from "@/utils/organograma/Organograma";
import { DatePickerElaboration } from "../../Inputs/DatePicker";
import { SelectFieldElaboration } from "../../Inputs/SelectField";
import { CreateUnityElaboration } from "../../Modal/CreateUnidade";
import { RadioCheckedElaboration } from "../../Inputs/RadioButton";
import { ElaborationService } from "@/services/endpoints/elaboracao";
import { ReutilizarElaborationDialog } from "../Vision/ReutilizarModal";
import { useSnackbarElaboration } from "@/hooks/useSnackbarElaboration";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { MultipleSelect } from "@/components/global/Inputs/MultipleInput";
import { RelacionamentoJsonService } from "@/services/endpoints/hierarquia";
import { addItemToNodeOrganogram } from "@/utils/organograma/addItemToNode";
import { findingMenuItemKey } from "@/contexts/elaboracao_planos/constants";
import { DisclaimerReuseElaborationDialog } from "../Vision/disclaimerModal";
import { DashboardManagementPlansService } from "@/services/endpoints/dashboard";
import { useElaborationCardItem } from "@/services/zustand/elaboration_card_item";
import { useHierarchyPreparationOfPlans, usePreparationOfPlans } from "@/hooks/elaboracao_planos";
import { analisarTerritorioArray, encontrarCorrespondentes, handleContentRelatedToTerritorySelected, removeDuplicatesFromA } from "../utils";

type InputsProductsDeliveries = {
  produtos_entregas: any;
  is_optional?: boolean;
}

const SchemaProductsDeliveries = Yup.object().shape({
  produtos_entregas: Yup.array().of(
    Yup.object().shape({
      valor: Yup.string(),
      texto: Yup.string().required('Campo obrigatório'),
      orgao: Yup.string().required('Campo obrigatório'),
      setor: Yup.string().required('Campo obrigatório'),
      territorio: Yup.string().required('Campo obrigatório'),
      responsavel: Yup.string().required('Campo obrigatório'),
      prazo: Yup.date().nonNullable().required('Campo obrigatório').typeError('Data inválida'),
      indicadores: Yup.array().of(
        Yup.object().shape({
          fonte: Yup.string().required("Campo obrigatório"),
          indicador: Yup.string().required('Campo obrigatório'),
          polaridade: Yup.string().required('Campo obrigatório'),
          unidadeMedida: Yup.string().required('Campo obrigatório'),
          formulaCalculo: Yup.string().required('Campo obrigatório'),
          planejados: Yup.array().of(
            Yup.object().shape({
              planejado: Yup.string().required('Campo obrigatório'),
              prazo: Yup.date().nonNullable().required('Campo obrigatório').typeError('Data inválida'),
            })
          )
        })
      )
    })
  ).required(''),
});

const defaultValue = {
  id: '',
  tor: [],
  texto: '',
  valor: '',
  orgao: '',
  setor: '',
  responsavel: '',
  indicadores: [
    {
      id: '',
      fonte: '',
      indicador: '',
      polaridade: '',
      unidadeMedida: '',
      formulaCalculo: '',
      planejados: [
        {
          id: '',
          prazo: null,
          planejado: '',
        }
      ]
    }
  ]
}

const transformProductsDeliveriesData = (data: any[], label?: string) => {
  return data.map(item => {
    let tor = [] as any[]
    tor = item?.entregasTerritorio.map((m: any) => {
      if (m?.bairro) return { id: m?.bairro?.id, descricao: m?.bairro?.name };
      if (m?.regiaoAdministrativa) return { id: m?.regiaoAdministrativa?.id, descricao: m?.regiaoAdministrativa?.name };
      if (m?.areaPlanejamento) return { id: m?.areaPlanejamento?.id, descricao: m?.areaPlanejamento?.name };
      if (m?.regiaoPlanejamento) return { id: m?.regiaoPlanejamento?.id, descricao: m?.regiaoPlanejamento?.name };
      return null;
    }).filter(Boolean);

    const territorio = analisarTerritorioArray(item.entregasTerritorio);

    return {
      tor,
      territorio,
      id: item?.id,
      texto: item?.texto || '',
      orgao: item?.orgao?.id || '',
      setor: item?.setor?.id || '',
      prazo: item?.data ? dayjs(item?.data) : null,
      valor: item?.valor || '',
      responsavel: item?.responsavel || '',
      indicadores: item?.indicadores?.map((indicador: any) => ({
        id: indicador?.id,
        fonte: indicador?.fonte || '',
        indicador: indicador?.indicador || '',
        polaridade: indicador?.polaridade || '',
        unidadeMedida: indicador?.unidadeMedida || '',
        formulaCalculo: indicador?.formulaCalculo || '',
        planejados: indicador?.values?.map((planejado: any) => ({
          id: planejado?.id,
          prazo: planejado?.data ? dayjs(planejado?.data) : null,
          planejado: planejado?.planejado || ''
        }))
      }))
    };
  });
};

const transformProductsDeliveriesDataReuse = (item: any) => {
  let tor = [] as any[]

  tor = item?.entregasTerritorio.map((m: any) => {
    if (m?.bairro) return { id: m?.bairro?.id, descricao: m?.bairro?.name };
    if (m?.regiaoAdministrativa) return { id: m?.regiaoAdministrativa?.id, descricao: m?.regiaoAdministrativa?.name };
    if (m?.areaPlanejamento) return { id: m?.areaPlanejamento?.id, descricao: m?.areaPlanejamento?.name };
    if (m?.regiaoPlanejamento) return { id: m?.regiaoPlanejamento?.id, descricao: m?.regiaoPlanejamento?.name };
    return null;
  }).filter(Boolean);

  const territorio = analisarTerritorioArray(item.entregasTerritorio);

  return {
    texto: item?.texto || '',
    territorio,
    tor,
    valor: item?.valor || '',
    orgao: item?.orgao?.id || '',
    setor: item?.setor?.id || '',
    responsavel: item?.responsavel || '',
    prazo: item?.data ? dayjs(item?.data) : null,
    indicadores: item?.indicadores?.map((indicador: any) => ({
      id: indicador?.id,
      fonte: indicador?.fonte || '',
      indicador: indicador?.indicador || '',
      polaridade: indicador?.polaridade || '',
      unidadeMedida: indicador?.unidadeMedida || '',
      formulaCalculo: indicador?.formulaCalculo || '',
      planejados: indicador?.values?.map((planejado: any) => ({
        id: planejado?.id,
        prazo: planejado?.data ? dayjs(planejado?.data) : null,
        planejado: planejado?.planejado || ''
      }))
    }))
  };
};

export const ProductsAndDeliveriesForm: React.FC = () => {
  const {
    trigger,
    clearErrors,
    watch: watchProductsDeliveries,
    control: controlProductsDeliveries,
    register: registerProductsDeliveries,
    setValue: setValueProductsDeliveries,
    handleSubmit: handleSubmitProductsDeliveries,
    formState: { errors: errosProductsDeliveries }
  } = useForm<InputsProductsDeliveries>({
    resolver: yupResolver(SchemaProductsDeliveries),
    defaultValues: { produtos_entregas: [defaultValue] }
  });

  const { append: appendProductsDeliveries, update: updateProductsDeliveries, remove: removeProductsDeliveries } = useFieldArray({
    control: controlProductsDeliveries,
    name: "produtos_entregas"
  });

  const fields = watchProductsDeliveries('produtos_entregas');
  const is_optional = watchProductsDeliveries('is_optional') as unknown as string;

  const router = useRouter();
  const pathname = router.pathname;
  const routerId = router.query.id;
  const [page, setPage] = React.useState(1);
  const [label, setLabel] = React.useState('');
  const [data, setData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [disable, setDisable] = React.useState(false);
  const { tipo: tipoFluxo, planId } = useTipeOfFlow();
  const [filterData, setFilterData] = React.useState({
    setor: [] as any[],
    orgaoResponsavel: [] as any[],
    territorio: {} as Record<string, any>
  });
  const [openUnity, setOpenUnity] = React.useState(false);
  const [reutilizar, setReutilizar] = React.useState(false);
  const [deleteGoalId, setDeleteGoalId] = React.useState(0);
  const [objetivos, setObjetivos] = React.useState<any[]>([]);
  const [goalSelectedIndex, setGoalIndex] = React.useState(0);
  const [preview, setPreview] = React.useState<boolean>(false);
  const [disabeSubmit, setDisableSubmit] = React.useState(false);
  const [deletePlannedId, setDeletePlannedId] = React.useState(0);
  const { getAllFilters } = React.useMemo(() => new DashboardManagementPlansService(), []);
  const [deleteGoalIndex, setDeleteGoalIndex] = React.useState(0);
  const [openDisclaimer, setOpenDisclaimer] = React.useState(false);
  const [currentIndexEixo, setCurrentIndexEixo] = React.useState(0);
  const [deleteIndicatorId, setDeleteIndicatorId] = React.useState(0);
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [deletePlannedIndex, setDeletePlannedIndex] = React.useState(0);
  const [unidadesMedida, setUnidadesMedida] = React.useState<any[]>([]);
  const [openDeleteModal3, setOpenDeleteModal3] = React.useState(false);
  const [eixos_tematicos, setEixosTematicos] = React.useState<any[]>([]);
  const [indicadoresSelected, setIndicadores] = React.useState({} as any);
  const [deleteIndicatorIndex, setDeleteIndicatorIndex] = React.useState(0);
  const [openDeleteLinhaModal, setOpenDeleteLinhaModal] = React.useState(false);
  const { toggleErrorSnackbar, toggleSuccessSnackbar } = useSnackbarElaboration();
  const zustandSelectedCardItem = useElaborationCardItem(state => state.cardItem);
  const { itemsMenu, selectedItemsMenu, handleFetchProgressCodePlanId, setDefaultItemMenuId, setNavigable, completCode } = usePreparationOfPlans();

  const total = objetivos.length;

  const {
    PostProgressCode,
    NumberElaboration,
    GetUnidadesMedida,
    PostUnidadesMedida,
    IndicatorsElaboration,
    UpdateNumbersElaboration,
    DeleteNumbersElaboration,
    GetThematicAxisElaboration,
    DeleteIndicatorElaboration,
    GetProgramsEixoElaborations,
    GetProjectsEixoElaborations,
    ProductsDeliveriesElaboration,
    GetMacroactionsEixoElaborations,
    GetProductsDeliveriesElaborations,
    DeleteProductsDeliveriesElaborations,
    UpdateProductsDeliveriesElaborations,
  } = React.useMemo(() => new ElaborationService(), []);

  const handleExternalSubmit = async () => {
    const isValid = await trigger();

    if (isValid) {
      handleSubmitProductsDeliveries(onSubmit)({
        data: fields,
        nativeEvent: { submitter: { name: 'any' } }
      } as any);
    }
  };

  function getMessageErrorByNameRef(json: any, title: any) {
    const foundObject = json?.produtos_entregas?.filter((item: any) => Boolean(item))?.find((item: any) => item[title]);
    return foundObject ? foundObject[title].message : null;
  }

  function getMessageErrorByNameRefSubArray(json: any, title: any, index: any) {
    const foundObject = json?.produtos_entregas?.filter((item: any) => Boolean(item))?.map((item: any) => {
      return item?.indicadores?.find((subItem: any) => subItem[title]);
    })

    return foundObject && foundObject[index] ? foundObject[index][title].message : null;
  }

  function getMessageErrorByNameRefSubArrayPlanned(json: any, title: any, index: any) {
    if (Object.keys(json).length == 0) return null;

    const planejadosArray = json.produtos_entregas?.filter((item: any) => Boolean(item))?.flatMap((item: any) =>
      item?.indicadores?.flatMap((indicador: any) => indicador?.planejados)
    );

    return planejadosArray && planejadosArray[index] && planejadosArray[index][title] ? planejadosArray[index][title].message : null;
  }

  const handleChange = async (_: any, value: any) => {
    if (typeof _ == 'string') {
      setPage(value);
      return;
    }
    const isValid = await trigger();

    if (isValid) {
      handleSubmitProductsDeliveries(onSubmit)({
        data: fields,
        nativeEvent: { submitter: { name: 'any' } }
      } as any);
    }
    setPage(value);
  };

  const handleLeftClick = () => {
    handleExternalSubmit()
    setCurrentIndexEixo(currentIndexEixo === 0 ? eixos_tematicos.length - 1 : currentIndexEixo - 1);
  };

  const handleRightClick = () => {
    handleExternalSubmit()
    setCurrentIndexEixo(currentIndexEixo === eixos_tematicos.length - 1 ? 0 : currentIndexEixo + 1);
  };

  const filterSectors = (data: any, orgao: any) => {
    const findItem = data?.orgaoResponsavel?.find((item: any) => item?.id == orgao);
    const filterItems =
      data?.setor?.filter((item: any) => item.codUa == findItem?.codUa)
        .map((item: any) => ({ value: item?.id, name: item?.descricao }))

    return filterItems
  };

  const handleSaveUnity = (e: any) => {
    PostUnidadesMedida({ descricao: e }).then(res => {
      GetUnidadesMedida().then(res => {
        const { data } = res.data as any;
        setUnidadesMedida(data.map((item: any) => ({ value: item.unidade, name: item.unidade })));
        setOpenUnity(false);
      })
    })
  };

  React.useEffect(() => {
    if (!routerId) return;
    if (!(pathname.split('/').includes('elaboracao_planos') || pathname.split('/').includes('aprovacao_planos'))) return;

    if (routerId && pathname.split('/').includes('elaboracao_planos') || pathname.split('/').includes('aprovacao_planos')) {
      GetThematicAxisElaboration(routerId).then(res => {
        setEixosTematicos(res.data);
      });
    }
  }, [GetThematicAxisElaboration, pathname, routerId]);

  React.useEffect(() => {
    const eixoId = eixos_tematicos[currentIndexEixo]?.id;
    if (!routerId) return;
    if (!eixoId) return;
    if (!(pathname.includes('elaboracao_planos') || pathname.includes('aprovacao_planos'))) return;

    setLoading(true);

    const projetos = completCode.find(item => item.code == 'projetos' && item.skipped === false);
    const macroacoes = completCode.find(item => item.code == 'macroacoes' && item.skipped === false);
    const programas = completCode.find(item => item.code == 'programas' && item.skipped === false);

    const fetchObjectives = () => {
      if (programas !== undefined && macroacoes != undefined && projetos == undefined) {
        setLabel('Programa');
        return GetProgramsEixoElaborations(routerId, eixoId);
      }

      if (programas != undefined && macroacoes == undefined && projetos == undefined) {
        setLabel('Programa');
        return GetProgramsEixoElaborations(routerId, eixoId);
      }

      if (macroacoes != undefined && programas == undefined && projetos == undefined) {
        setLabel('Macroação');
        return GetMacroactionsEixoElaborations(routerId, eixoId);
      }

      setLabel('Projeto');
      return GetProjectsEixoElaborations(routerId, eixoId);
    }

    fetchObjectives()
      .then(res => setObjetivos(res.data))
      .finally(() => setLoading(false));
  }, [GetMacroactionsEixoElaborations, GetProgramsEixoElaborations, GetProjectsEixoElaborations, completCode, currentIndexEixo, eixos_tematicos, pathname, routerId]);

  React.useEffect(() => {
    if (!routerId) return;
    if (!(pathname.split('/').includes('elaboracao_planos') || pathname.split('/').includes('aprovacao_planos'))) return;

    const current = objetivos[page - 1];
    clearErrors()

    if (label == 'Macroação') {
      if (current?.entregas?.length > 0) {
        setValueProductsDeliveries('produtos_entregas', transformProductsDeliveriesData(current.entregas, label));
      } else {
        setValueProductsDeliveries('produtos_entregas', [defaultValue])
      }
    }

    if (label == 'Projeto') {
      if (current?.entregasProjetos?.length > 0) {
        setValueProductsDeliveries('produtos_entregas', transformProductsDeliveriesData(current.entregasProjetos, label));
      } else {
        setValueProductsDeliveries('produtos_entregas', [defaultValue])
      }
    }
    if (label == 'Programa') {
      if (current?.entregas?.length > 0) {
        setValueProductsDeliveries('produtos_entregas', transformProductsDeliveriesData(current.entregas, label));
      } else {
        setValueProductsDeliveries('produtos_entregas', [defaultValue])
      }
    }
  }, [clearErrors, label, objetivos, page, pathname, routerId, setValueProductsDeliveries]);

  React.useEffect(() => {
    if (zustandSelectedCardItem.title != 'Plano de Gestão' && zustandSelectedCardItem.title != 'Plano Orçamentário') {
      setValueProductsDeliveries("is_optional", false)
    }
  }, [setValueProductsDeliveries, zustandSelectedCardItem.title]);

  React.useEffect(() => {
    if (!routerId) return;
    if (tipoFluxo != 'substituir') return;
    if (!(pathname.split('/').includes('elaboracao_planos') || pathname.split('/').includes('aprovacao_planos'))) return;

    if (routerId && (pathname.split('/').includes('elaboracao_planos') || pathname.split('/').includes('aprovacao_planos')) && tipoFluxo == 'substituir') {
      GetProductsDeliveriesElaborations(routerId).then(res => {
        setData(res.data);
      });
    }
  }, [GetProductsDeliveriesElaborations, pathname, routerId, tipoFluxo]);

  React.useEffect(() => {
    const code = completCode.find(item => item.code == 'produtos_entregas');

    if (code?.code?.length > 0) {
      setDisable(true);
    } else {
      setDisable(false)
    }
  }, [completCode]);

  React.useEffect(() => {
    const code = completCode.find(item => item.code == 'produtos_entregas' && item.skipped == false);

    if (code != undefined) {
      setValueProductsDeliveries('is_optional', true)
    }
  }, [completCode, setValueProductsDeliveries]);

  React.useEffect(() => {
    if (zustandSelectedCardItem.title == "Plano de Gestão" || zustandSelectedCardItem.title == 'Plano Orçamentário') {
      setValueProductsDeliveries('is_optional', true);
    }
  }, [setValueProductsDeliveries, zustandSelectedCardItem.title])

  React.useEffect(() => {
    if (!router.isReady) return

    setPreview(router.asPath.includes('preview'));
  }, [router.asPath, router.isReady]);

  React.useEffect(() => {
    if (pathname.split('/').includes('elaboracao_planos') || pathname.split('/').includes('aprovacao_planos'))
      getAllFilters().then((res: any) => {
        const { data } = res.data as any;
        setFilterData(data);
      })
  }, [getAllFilters, pathname]);

  React.useEffect(() => {
    GetUnidadesMedida().then(res => {
      const { data } = res.data as any;
      setUnidadesMedida(data.map((item: any) => ({ value: item.unidade, name: item.unidade })));
    })
  }, [GetUnidadesMedida]);

  const { consultarOrganograma, salvarOrganograma, startOrganograma } = new Organograma(router.query.id as unknown as number);

  const { insertItemById, updateItemById, deleteItemById } = useHierarchyPreparationOfPlans();
  const { startRelacionamento, consultarRelacionamento, atualizarRelacionamento } = new RelacionamentoJsonService(router.query.id as unknown as number);

  const onSubmit: SubmitHandler<any> = async (data, event: any) => {
    const { produtos_entregas } = data;
    const produtos_entregas_code = completCode.find(item => item.code == 'produtos_entregas')

    if (disabeSubmit) return;

    setDisableSubmit(true);

    let submittedData = produtos_entregas;
    if (event?.data?.length > 0) {
      submittedData = event.data;
    }

    let eixo = '';
    let layerName = '';
    if (label === 'Macroação') {
      eixo = 'macroId';
      layerName = 'macroacoes';
    };

    if (label === 'Programa') {
      eixo = 'programaId';
      layerName = 'programas';
    };

    if (label === 'Projeto') {
      eixo = 'projetosId';
      layerName = 'projetos';
    };

    try {
      // Processamento sequencial dos indicadores
      for (const [indexProducts, itemMeta] of submittedData.entries()) {
        for (const [indexIndicador, itemIndicador] of itemMeta.indicadores.entries()) {
          const { planejados, unidadeMedida: uni, id, ...rest } = itemIndicador;

          // Construir o objeto do indicador
          const indicador = isNumber(id)
            ? {
              ...rest,
              id,
              planoId: router.query.id,
              unidadeMedida: unidadesMedida.find(item => item.value === uni)?.name
            }
            : {
              ...rest,
              planoId: router.query.id,
              unidadeMedida: unidadesMedida.find(item => item.value === uni)?.name
            };

          try {
            // Realizar a requisição de elaboração do indicador
            const res = await IndicatorsElaboration(indicador);
            const data = res.data;

            // Atualizar o ID do indicador no formulário
            setValueProductsDeliveries(`produtos_entregas.${indexProducts}.indicadores.${indexIndicador}.id`, data.id);

            // Atualizar o ID do indicador no array `submittedData`
            itemIndicador.id = data.id;
          } catch (error) {
            console.error('Erro ao elaborar indicador:', error);
          }
        }
      }

      // Após garantir que os IDs dos indicadores estão disponíveis, processar as metas
      for (const [indexProduct, itemMeta] of fields.entries()) {
        const { indicadores, tor, id, valor, ...rest } = itemMeta;

        // Criar o objeto de meta com os IDs dos indicadores atualizados
        let produto_entrega = {
          ...rest,
          planoId: router.query.id,
          [eixo]: objetivos[page - 1]?.id,
          eixo_tematico: eixos_tematicos[currentIndexEixo]?.id,
          indicadoresIds: indicadores.map((item: any) => item.id), // IDs agora garantidos
          prazo: dayjs(itemMeta.prazo).format('YYYY-MM-DD') + 'T00:00:00',
          territorio: encontrarCorrespondentes(itemMeta.tor, filterData.territorio),
        };

        if (valor.length > 0) {
          produto_entrega = { ...produto_entrega, valor }
        }

        try {
          if (itemMeta.id) {
            await UpdateProductsDeliveriesElaborations(produto_entrega, itemMeta.id);
          } else {
            const response = await ProductsDeliveriesElaboration(produto_entrega, router.query.id);

            let count = 0;
            for (const item of [response?.data?.data]) {
              count += 1;

              if (!fields.some((item: any) => typeof item.id == 'number') && count == 1) {
                try {
                  const relacionamento = await consultarRelacionamento();
                  const root = JSON.parse(JSON.parse(relacionamento));

                  const newInsertItem = updateItemById({
                    root,
                    targetId: objetivos[page - 1]?.id,
                    item: { entregas: { itens: [] } },
                    layerName,
                  });

                  startRelacionamento(JSON.stringify(newInsertItem));
                  await atualizarRelacionamento(); // Aguarda o término da atualização

                  const relacionamentoV2 = await consultarRelacionamento();
                  const rootV2 = JSON.parse(JSON.parse(relacionamentoV2));

                  const newInsertItemV2 = insertItemById({
                    root: rootV2,
                    targetId: objetivos[page - 1]?.id,
                    item: { id: response?.data?.data, relacionamento: {} },
                    layerName: 'entregas',
                  });

                  startRelacionamento(JSON.stringify(newInsertItemV2));
                  await atualizarRelacionamento(); // Aguarda o término da atualização
                  setValueProductsDeliveries(`produtos_entregas.${indexProduct}.id`, response.data.data)
                } catch (error) {
                  console.error(`Erro ao atualizar a camada de entregas para o item ${item?.id}:`, error);
                }
              } else {
                try {
                  const relacionamento = await consultarRelacionamento();
                  const root = JSON.parse(JSON.parse(relacionamento));

                  const newInsertItem = insertItemById({
                    root,
                    targetId: objetivos[page - 1]?.id,
                    item: { id: response?.data?.data, relacionamento: {} },
                    layerName: 'entregas',
                  });

                  startRelacionamento(JSON.stringify(newInsertItem));
                  await atualizarRelacionamento(); // Aguarda o término da atualização
                } catch (error) {
                  console.error(`Erro ao inserir o item na camada de entregas para o item ${item?.id}:`, error);
                }
              }
            }
          }
        } catch (error) {
          console.error('Erro ao processar produtos/entregas:', error);
        }

        // Processar os planejamentos de cada indicador
        for (const [indexIndicador, indicadorItem] of indicadores.entries()) {
          for (const [indexPlanejado, planejadoItem] of indicadorItem.planejados.entries()) {
            const planejado = {
              planoId: Number(router.query.id),
              planejado: Number(planejadoItem.planejado),
              indicadorId: indicadorItem.id, // O ID está garantido aqui
              date: dayjs(planejadoItem.prazo).format('YYYY-MM-DD') + 'T00:00:00',
            };

            try {
              // Verificar se o planejamento já existe e realizar a atualização ou criação
              if (isNumber(planejadoItem.id)) {
                await UpdateNumbersElaboration(planejadoItem.id, planejado);
              } else {
                await NumberElaboration(planejado);
              }
            } catch (error) {
              console.error('Erro ao processar planejamento:', error);
            }
          }
        }
      }

      toggleSuccessSnackbar();
      setTimeout(() => toggleSuccessSnackbar(), 3000);
    } catch {
      toggleErrorSnackbar();
      setDisableSubmit(false);
    } finally {
      if (event.nativeEvent.submitter.name == 'next_step') {
        setNavigable(true);
        if (router.pathname.includes('epl')) {
          router.push('dashboard/aprovacao_planos/' + router.query.id + '/epl/preview')
        } else {
          router.push('/dashboard/elaboracao_planos/' + router.query.id);
        }
        const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
        const planId = plan[selectedItemsMenu?.layer as string]
          .find((item: any) => item.id == selectedItemsMenu?.id).id + 1;
        setDefaultItemMenuId(planId);
      } else {
        setNavigable(false);
        const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
        const planId = plan[selectedItemsMenu?.layer as string]
          .find((item: any) => item.id == selectedItemsMenu?.id).id;
        setDefaultItemMenuId(planId);
        if (router.pathname.includes('epl')) {
          router.push('dashboard/aprovacao_planos/' + router.query.id + '/epl/preview')
        } else {
          router.push('/dashboard/elaboracao_planos/' + router.query.id);
        }
        handleFetchProgressCodePlanId(router.query.id);
      }
      setDisableSubmit(false);
    }

    if (produtos_entregas_code != undefined) return;

    try {
      const response = await consultarOrganograma();
      const organograma = response.Organograma;

      const newItem = {
        "tooltip": "",
        "position": "",
        "manager": label,
        "name": "Produtos/Entregas",
        "subordinates": [],
      };

      const newOrganograma = addItemToNodeOrganogram(organograma, label, newItem);
      startOrganograma(newOrganograma);
      salvarOrganograma();

    } catch {
    }
  };

  const ThematicAxisBanner = () => {
    if (Boolean(is_optional) == false || is_optional == undefined) return null;
    if (is_optional == 'false' || is_optional == undefined) return null;

    return (
      <>
        <Grid item xs={12}>
          <div style={{ height: '1px', background: '#B4B4B4' }}></div>
        </Grid>
        <Grid item xs={12} className="goals_select_content_wrapper">
          <p>Eixo temático</p>
        </Grid>
        <Grid item xs={12}>
          <div className="goals_select_wrapper">
            <div className="goals_left_arrow" onClick={() => handleLeftClick()}>{"<"}</div>
            <div className="goals_select_content_wrapper">
              <p>{eixos_tematicos[currentIndexEixo]?.texto}</p>
              <span>Eixo temático {currentIndexEixo + 1} de {eixos_tematicos.length}</span>
            </div>
            <div className="goals_rigth_arrow" onClick={() => handleRightClick()}>{">"}</div>
          </div>
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <div className="pagination_wrapper">
            <Pagination
              count={total}
              page={page}
              onChange={handleChange}
              hidePrevButton hideNextButton
              variant="outlined" shape="rounded"
            />
            <p>Página {page} de {total}</p>
          </div>
        </Grid>
        {objetivos.length == 0 ? null : (
          <Grid item xs={12}>
            <div className="strategy_wrapper">
              <p>
                <b>{label}:</b>&nbsp;
                {objetivos[page - 1]?.texto}
              </p>
            </div>
          </Grid>
        )}
      </>
    );
  }

  interface ErrorMessageProps {
    errors: any;
    name: string;
    index: number; // Adicione o índice para garantir que está validando o erro correto
  }

  const ErrorMessage: React.FC<ErrorMessageProps> = ({ errors, name, index }) => {
    const error = errors?.produtos_entregas?.[index]?.[name];
    return error ? <div className="error_wrapper">
      <span>{error.message}</span>
    </div> : null;
  };

  interface ErrorMessagePropsIndicator {
    errors: any;
    name: string; // Nome do campo para validar
    metaIndex: number; // Índice do item em "meta"
    indicadorIndex: number; // Índice do item em "indicadores"
  }

  const ErrorMessageIndicator: React.FC<ErrorMessagePropsIndicator> = ({ errors, name, metaIndex, indicadorIndex }) => {
    // Acessa o erro específico
    const error = errors?.produtos_entregas?.[metaIndex]?.indicadores?.[indicadorIndex]?.[name];
    return error ? <div className="error_wrapper" style={{ marginTop: '' }}>
      <span>{error.message}</span>
    </div> : null;
  };

  interface ErrorMessagePropsPlanned {
    errors: any;
    name: string; // Nome do campo para validar
    metaIndex: number; // Índice do item em "meta"
    indicadorIndex: number; // Índice do item em "indicadores"
    plannedIndex: number
  }


  const ErrorMessagePlanned: React.FC<ErrorMessagePropsPlanned> = ({ errors, name, metaIndex, indicadorIndex, plannedIndex }) => {
    // Acessa o erro específico
    const error = errors?.produtos_entregas?.[metaIndex]?.indicadores?.[indicadorIndex]?.planejados?.[plannedIndex]?.[name];
    return error ? <div className="error_wrapper" style={{ marginTop: '' }}>
      <span>{error.message}</span>
    </div> : null;
  };

  const Fields = () => {
    if (Boolean(is_optional) == false || is_optional == undefined) return null;
    if (is_optional == 'false' || is_optional == undefined) return null;
    if (objetivos.length == 0) return null

    return (
      <>
        <Grid item xs={12} sx={{ ml: 2 }}>
          {fields?.map((field: any, goalIndex: any) => {
            const tor = watchProductsDeliveries(`produtos_entregas.${goalIndex}.tor`);
            const orgao = watchProductsDeliveries(`produtos_entregas.${goalIndex}.orgao`);
            const setor = watchProductsDeliveries(`produtos_entregas.${goalIndex}.setor`);
            const prazo = watchProductsDeliveries(`produtos_entregas.${goalIndex}.prazo`);
            const texto = watchProductsDeliveries(`produtos_entregas.${goalIndex}.texto`);
            const territorio = watchProductsDeliveries(`produtos_entregas.${goalIndex}.territorio`);
            const responsavel = watchProductsDeliveries(`produtos_entregas.${goalIndex}.responsavel`);
            const publicoAlvo = watchProductsDeliveries(`produtos_entregas.${goalIndex}.publicoAlvo`);

            return (
              <React.Fragment key={field?.id || `${goalIndex}-${texto}`}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    mt: 2,
                    borderRadius: '5px',
                    background: '#DFDFDF',
                    padding: '0 1rem  0 0'
                  }}
                >
                  <Grid item xs={12}>
                    <input
                      type="hidden"
                      defaultValue={field?.id}
                      {...registerProductsDeliveries(`produtos_entregas.${goalIndex}.id`, { value: field?.id })}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <DatePickerElaboration
                      width="90%"
                      label="Prazo"
                      commentTittle={texto}
                      formName="Produto/Entrega"
                      required={true}
                      defaultValue={prazo}
                      errors={errosProductsDeliveries}
                      control={controlProductsDeliveries as any}
                      name={`produtos_entregas.${goalIndex}.prazo`}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="prazo" index={goalIndex} />

                  </Grid>
                  <Grid item xs={12}>
                    <TextFieldElaboration
                      rows={3}
                      label="Produto/Entrega"
                      multiline={true}
                      required={true}
                      formName="Produto/Entrega"
                      commentTittle={texto}
                      errors={errosProductsDeliveries}
                      defaultValue={texto}
                      register={registerProductsDeliveries}
                      placeholder="Digite a descrição do produto/entrega"
                      name={`produtos_entregas.${goalIndex}.texto`}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="texto" index={goalIndex} />

                  </Grid>
                  <Grid item xs={12}>
                    <SelectFieldElaboration
                      required={true}
                      label="Território"
                      formName="Produto/Entrega"
                      commentTittle={texto}
                      defaultValue={territorio}
                      errors={errosProductsDeliveries}
                      register={registerProductsDeliveries}
                      placeholder="Selecione o território"
                      data={[
                        { value: 0, name: '-' },
                        { value: 1, name: 'AP' },
                        { value: 2, name: 'RP' },
                        { value: 3, name: 'RA' },
                        { value: 4, name: 'Bairros' }
                      ]}
                      name={`produtos_entregas.${goalIndex}.territorio`}
                      onClick={() => updateProductsDeliveries(goalIndex, { ...field, tor: [] })}
                      setValue={(value) => setValueProductsDeliveries(`produtos_entregas.${goalIndex}.territorio`, value)}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="territorio" index={goalIndex} />

                  </Grid>
                  {territorio != 0 && (
                    <Grid item xs={12}>
                      <MultipleSelect
                        fullWidth={true}
                        value={Array.isArray(tor) ? tor : []}
                        setValue={(e) => {
                          const items = removeDuplicatesFromA(tor, e)
                          updateProductsDeliveries(goalIndex, { ...field, tor: items })
                        }}
                        {...registerProductsDeliveries(`produtos_entregas.${goalIndex}.tor`, { required: false })}
                        data={handleContentRelatedToTerritorySelected(filterData, territorio, tor)}
                      />
                    </Grid>
                  )}
                  <Grid item xs={3}>
                    <TextFieldElaboration
                      required={false}
                      type='number'
                      label="Valor"
                      formName="Produto/Entrega"
                      commentTittle={texto}
                      defaultValue={publicoAlvo}
                      placeholder="Digite o valor"
                      errors={errosProductsDeliveries}
                      register={registerProductsDeliveries}
                      name={`produtos_entregas.${goalIndex}.valor`}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="valor" index={goalIndex} />

                  </Grid>
                  <Grid item xs={3}>
                    <SelectFieldElaboration
                      label="Órgão"
                      required={true}
                      defaultValue={orgao}
                      commentTittle={texto}
                      formName="Produto/Entrega"
                      placeholder="Selecione"
                      errors={errosProductsDeliveries}
                      register={registerProductsDeliveries}
                      name={`produtos_entregas.${goalIndex}.orgao`}
                      data={filterData.orgaoResponsavel.map((item) => ({ value: item.id, name: item.sigla }))}
                      setValue={(value) => setValueProductsDeliveries(`produtos_entregas.${goalIndex}.orgao`, value)}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="orgao" index={goalIndex} />

                  </Grid>
                  <Grid item xs={3}>
                    <SelectFieldElaboration
                      label="Setor"
                      required={true}
                      defaultValue={setor}
                      formName="Produto/Entrega"
                      commentTittle={texto}
                      placeholder="Selecione"
                      errors={errosProductsDeliveries}
                      register={registerProductsDeliveries}
                      data={filterSectors(filterData, orgao)}
                      name={`produtos_entregas.${goalIndex}.setor`}
                      setValue={(value) => setValueProductsDeliveries(`produtos_entregas.${goalIndex}.setor`, value)}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="setor" index={goalIndex} />

                  </Grid>
                  <Grid item xs={3} sx={{ mt: -.3 }}>
                    <TextFieldElaboration
                      required={true}
                      errors={errosProductsDeliveries}
                      formName="Produto/Entrega"
                      label="Responsável"
                      register={registerProductsDeliveries}
                      defaultValue={responsavel}
                      commentTittle={texto}
                      placeholder="Nome do responsável"
                      name={`produtos_entregas.${goalIndex}.responsavel`}
                    />
                    <ErrorMessage errors={errosProductsDeliveries} name="responsavel" index={goalIndex} />

                  </Grid>
                  <Grid item xs={12} sx={{ mb: 2 }}>
                    <p className="sub_item_topographic">
                      Indicadores
                    </p>
                  </Grid>
                  {field?.indicadores?.map((indicator: any, indicatorIndex: any, arr: any[]) => {
                    const randomIdIndicadores = Math.random();
                    const lastIdesOfIndicator = arr.lastIndexOf(arr[arr.length - 1]);
                    const fonte = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.fonte`);
                    const indicador = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.indicador`);
                    const unidade = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.unidadeMedida`);
                    const polaridade = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.polaridade`);
                    const formula = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.formulaCalculo`);

                    return (
                      <React.Fragment key={field?.id || `${indicatorIndex}-${indicador}`}>
                        <Grid
                          container
                          spacing={2}
                          className="sub_container"
                          sx={{ background: '#fff', margin: '0 0 1rem 1rem', padding: '0 1rem 1rem 1rem ' }}
                        >
                          <Grid item xs={12} sx={{ mt: 2 }}>
                            <TextFieldElaboration
                              required={true}
                              label="Indicador"
                              commentTittle={texto}
                              defaultValue={indicador}
                              formName="Produto/Entrega"
                              placeholder="Digite o indicador"
                              errors={errosProductsDeliveries}
                              register={registerProductsDeliveries}
                              name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.indicador`}
                            />
                            <ErrorMessageIndicator
                              errors={errosProductsDeliveries}
                              name="indicador"
                              metaIndex={goalIndex}
                              indicadorIndex={indicatorIndex}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <SelectFieldElaboration
                              required={true}
                              data={unidadesMedida}
                              commentTittle={texto}
                              defaultValue={unidade}
                              formName="Produto/Entrega"
                              label="Unidade de medida"
                              errors={errosProductsDeliveries}
                              register={registerProductsDeliveries}
                              handleCreate={() => setOpenUnity(true)}
                              placeholder="Crie uma nova unidade de medida"
                              name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.unidadeMedida`}
                              setValue={(value) => setValueProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.unidadeMedida`, value)}
                            />
                            <ErrorMessageIndicator
                              errors={errosProductsDeliveries}
                              name="unidadeMedida"
                              metaIndex={goalIndex}
                              indicadorIndex={indicatorIndex}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <TextFieldElaboration
                              required={true}
                              formName="Produto/Entrega"
                              placeholder="Digite"
                              defaultValue={formula}
                              commentTittle={texto}
                              label="Fórmula de cálculo"
                              errors={errosProductsDeliveries}
                              register={registerProductsDeliveries}
                              name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.formulaCalculo`}
                            />
                            <ErrorMessageIndicator
                              errors={errosProductsDeliveries}
                              name="formulaCalculo"
                              metaIndex={goalIndex}
                              indicadorIndex={indicatorIndex}
                            />
                          </Grid>
                          <Grid item xs={3} sx={{ mt: 0 }}>
                            <TextFieldElaboration
                              label="Fonte"
                              required={true}
                              placeholder="Digite"
                              commentTittle={texto}
                              defaultValue={fonte}
                              formName="Produto/Entrega"
                              errors={errosProductsDeliveries}
                              register={registerProductsDeliveries}
                              name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.fonte`}
                            />
                            <ErrorMessageIndicator
                              errors={errosProductsDeliveries}
                              name="fonte"
                              metaIndex={goalIndex}
                              indicadorIndex={indicatorIndex}
                            />
                          </Grid>
                          <Grid item xs={3} sx={{ mt: 0 }}>
                            <SelectFieldElaboration
                              required={true}
                              label="Polaridade"
                              commentTittle={texto}
                              formName="Produto/Entrega"
                              placeholder="Selecione"
                              defaultValue={polaridade}
                              errors={errosProductsDeliveries}
                              register={registerProductsDeliveries}
                              name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.polaridade`}
                              data={[{ value: 'Negativo', name: 'Negativo' }, { value: 'Positivo', name: 'Positivo' }, { value: 'Neutro', name: 'Neutro' }]}
                              setValue={(value) => setValueProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.polaridade`, value)}
                            />
                            <ErrorMessageIndicator
                              errors={errosProductsDeliveries}
                              name="polaridade"
                              metaIndex={goalIndex}
                              indicadorIndex={indicatorIndex}
                            />
                          </Grid>
                          {indicator?.planejados?.map((plannedField: any, plannedIndex: any, array: any) => {
                            const randomIdPlanejados = Math.random();
                            const lastIdesOfPlanned = array.lastIndexOf(array[array.length - 1]);
                            const id = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.planejados.${plannedIndex}.id`);
                            const prazo = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.planejados.${plannedIndex}.prazo`);
                            const planejado = watchProductsDeliveries(`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.planejados.${plannedIndex}.planejado`);
                            return (
                              <React.Fragment key={plannedField?.id || `${plannedIndex}-${id}`}>
                                <Grid item xs={12}>
                                  <input
                                    type="hidden"
                                    {
                                    ...registerProductsDeliveries(
                                      `produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.planejados.${plannedIndex}.id`,
                                      { value: id })}
                                    defaultValue={id}
                                  />
                                </Grid>
                                <Grid item xs={3}>
                                  <DatePickerElaboration
                                    width="90%"
                                    commentTittle={texto}
                                    label="Prazo"
                                    formName="Produto/Entrega"
                                    required={true}
                                    defaultValue={prazo}
                                    errors={errosProductsDeliveries}
                                    control={controlProductsDeliveries as any}
                                    name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.planejados.${plannedIndex}.prazo`}
                                  />
                                  <ErrorMessagePlanned
                                    errors={errosProductsDeliveries}
                                    name="prazo"
                                    plannedIndex={plannedIndex}
                                    metaIndex={goalIndex}
                                    indicadorIndex={indicatorIndex}
                                  />
                                </Grid>
                                <Grid item xs={9}>
                                  <TextFieldElaboration
                                    type='number'
                                    required={true}
                                    label="Planejado"
                                    formName="Produto/Entrega"
                                    errors={errosProductsDeliveries}
                                    commentTittle={texto}
                                    defaultValue={planejado}
                                    register={registerProductsDeliveries}
                                    placeholder="Digite o valor do planejado para o indicador da produtos_entregas"
                                    name={`produtos_entregas.${goalIndex}.indicadores.${indicatorIndex}.planejados.${plannedIndex}.planejado`}
                                  />
                                <ErrorMessagePlanned
                                    errors={errosProductsDeliveries}
                                    name="planejado"
                                    plannedIndex={plannedIndex}
                                    metaIndex={goalIndex}
                                    indicadorIndex={indicatorIndex}
                                  />
                                </Grid>
                                <Grid item xs={12} sx={{ mb: 2, display: 'flex', justifyContent: lastIdesOfPlanned == plannedIndex ? 'space-between' : 'flex-end' }}>
                                  {lastIdesOfPlanned == plannedIndex && (
                                    <div className="add_line_wrapper">
                                      <p onClick={() => {
                                        const newPlanejados = [
                                          ...indicator.planejados,
                                          { prazo: null, planejado: '' },
                                        ];
                                        updateProductsDeliveries(goalIndex, {
                                          ...field,
                                          indicadores: field.indicadores.map((ind: any, i: number) => i === indicatorIndex ? { ...ind, planejados: newPlanejados } : ind)
                                        });
                                      }}>
                                        {`+ Adicionar linha`}
                                      </p>
                                    </div>
                                  )}
                                  {plannedIndex != 0 && (
                                    <div
                                      className="remove_line_wrapper"
                                      onClick={() => {
                                        setOpenDeleteLinhaModal(true);
                                        setDeletePlannedIndex(plannedIndex);
                                        setDeletePlannedId(plannedField.id);
                                        setGoalIndex(goalIndex)
                                        setIndicadores(indicator)
                                      }}
                                    >
                                      <TrashIcon />
                                      <p>Excluir</p>
                                    </div>
                                  )}
                                </Grid>
                                <DeleteElaborationDialog open={openDeleteLinhaModal} setOpen={(e) => setOpenDeleteLinhaModal(e)} onDelete={() => {
                                  const newPlanejados = indicadoresSelected.planejados.filter((_: any, i: any) => i != deletePlannedIndex);

                                  updateProductsDeliveries(goalSelectedIndex, {
                                    ...field,
                                    indicadores: field.indicadores.map((ind: any, i: number) => i === indicatorIndex ? { ...ind, planejados: newPlanejados } : ind)
                                  });

                                  if (deletePlannedId != 0) {
                                    DeleteNumbersElaboration(deletePlannedId);
                                    setDeletePlannedId(0)
                                  }

                                  setOpenDeleteLinhaModal(false);
                                }} />
                              </React.Fragment>
                            )
                          })}
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2, mt: -2, display: 'flex', justifyContent: lastIdesOfIndicator == indicatorIndex ? 'space-between' : 'flex-end' }}>
                          {lastIdesOfIndicator == indicatorIndex && (
                            <div className="add_line_wrapper">
                              <p onClick={() => {
                                const newIndicadores = [
                                  ...field.indicadores,
                                  {
                                    fonte: '',
                                    indicador: '',
                                    polaridade: '',
                                    unidadeMedida: '',
                                    formula_calculo: '',
                                    planejados: [
                                      {
                                        prazo: null,
                                        planejado: '',
                                      }
                                    ]
                                  }
                                ];
                                updateProductsDeliveries(goalIndex, { ...field, indicadores: newIndicadores });
                              }}>
                                {`+ Adicionar indicador para produtos_entregas`}
                              </p>
                            </div>

                          )}
                          {indicatorIndex != 0 && !preview && (
                            <div
                              className="remove_line_wrapper"
                              onClick={() => {
                                setOpenDeleteModal(true);
                                setDeleteIndicatorIndex(indicatorIndex)
                                setDeleteIndicatorId(field.indicadores[indicatorIndex]?.id)
                              }}
                            >
                              <TrashIcon />
                              <p>Excluir</p>
                            </div>
                          )}
                        </Grid>
                        <DeleteElaborationDialog open={openDeleteModal} setOpen={(e) => setOpenDeleteModal(e)} onDelete={() => {
                          const newIndicadores = field.indicadores.filter((_: any, i: any) => i !== deleteIndicatorIndex);
                          updateProductsDeliveries(goalIndex, { ...field, indicadores: newIndicadores });

                          if (deleteIndicatorId != 0) {
                            DeleteIndicatorElaboration(deleteIndicatorId);
                            setDeleteIndicatorId(0)
                          }

                          setOpenDeleteModal(false);
                        }} />
                      </React.Fragment>
                    )
                  })}
                  {goalIndex != 0 && (
                    <Grid item xs={12} sx={{ mb: 1 }} display='flex' justifyContent='flex-end'>
                      <div
                        className="remove_line_wrapper"
                        onClick={() => {
                          setOpenDeleteModal3(true);
                          setDeleteGoalIndex(goalIndex);
                          setDeleteGoalId(field.id)
                        }}
                      >
                        <TrashIcon />
                        <p>Excluir</p>
                      </div>
                    </Grid>
                  )}
                  <DeleteElaborationDialog open={openDeleteModal3} setOpen={(e) => setOpenDeleteModal3(e)} onDelete={async () => {
                    removeProductsDeliveries(deleteGoalIndex);

                    if (deleteGoalId != 0) {
                      const relacionamento = await consultarRelacionamento();
                      const root = JSON.parse(JSON.parse(relacionamento));

                      const newItem = deleteItemById({ root, layerName: 'entregas', targetId: deleteGoalId });

                      startRelacionamento(JSON.stringify(newItem));
                      atualizarRelacionamento();

                      DeleteProductsDeliveriesElaborations(deleteGoalId);
                      setDeleteGoalId(0)
                    }

                    setOpenDeleteModal3(false);
                  }} />
                </Grid>
              </React.Fragment>
            );

          })}
          <Grid item xs={12} sx={{ ml: -2 }}>
            <div className="add_line_wrapper">
              <p onClick={() => appendProductsDeliveries(defaultValue)}>
                {`+ Adicionar produtos/entregas`}
              </p>
            </div>
          </Grid>
        </Grid>
      </>
    );
  };

  const ReplaceButton = () => {
    if (is_optional == 'false' || is_optional == undefined && (zustandSelectedCardItem.title != 'Plano de Gestão' && zustandSelectedCardItem.title != 'Plano Orçamentário')) return null;
    if (tipoFluxo != 'substituir') return null;

    return (
      <Grid item xs={4}>
        <button className="recicle_content_button" type='button' onClick={() => {
          if (data.length > 0) {
            setReutilizar(true);
          } else {
            setOpenDisclaimer(true);
          }
        }}>
          <p>Reutilizar conteúdo</p>
        </button>
      </Grid>
    );
  };

  const SubmitButton = () => {
    const opitional = () => {
      if (typeof is_optional == 'string') {
        return is_optional == 'true' ? 'true' : 'false'
      } else {
        return Boolean(is_optional) == true ? 'true' : 'false'
      }
    }

    const optionalV2 = () => {
      if (typeof is_optional == 'string') {
        return is_optional == 'false' || is_optional == undefined;
      } else {
        return Boolean(is_optional) == false || is_optional == undefined;
      }
    }
    return (
      <>
        <Grid item xs={12}>
          <div style={{ height: '1px', background: '#B4B4B4' }}></div>
        </Grid>
        <Grid item xs={12}>
          <ActionButtonElaborations disable={disabeSubmit} layer_indicator={false} isOptional={opitional()} onClick={() => {
            if (optionalV2()) {
              setNavigable(true)
              PostProgressCode({ code: 'produtos_entregas', skipped: true }, router.query.id)
              router.push('/dashboard/elaboracao_planos/' + router.query.id);

              const plan = itemsMenu[findingMenuItemKey(zustandSelectedCardItem)] as Record<string, any>;
              if (plan && plan[selectedItemsMenu?.layer as string]) {
                const planId = plan[selectedItemsMenu?.layer as string]
                  .find((item: any) => item.id == selectedItemsMenu?.id).id + 1;
                setDefaultItemMenuId(planId);
              }
            }
          }} />
        </Grid>
      </>
    );
  };

  if (loading) {
    return <LoadingBuffer />
  }

  return (
    <form onSubmit={handleSubmitProductsDeliveries(onSubmit)}>
      <Container>
        <Grid container spacing={2}>
          {(zustandSelectedCardItem.title == 'Plano Setorial' || zustandSelectedCardItem.title == 'Plano Institucional' || zustandSelectedCardItem.title != 'Plano de Gestão' && zustandSelectedCardItem.title != 'Plano Orçamentário') && (
            <Grid item xs={12}>
              <RadioCheckedElaboration
                required={false}
                name="is_optional"
                disabled={disable}
                errors={errosProductsDeliveries}
                control={controlProductsDeliveries as any}
                defaultValue={is_optional == 'true' ? 'true' : 'false'}
                data={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]}
                label="Este elemento é opcional para o tipo de plano selecionado. Deseja preencher a informação?"
              />
            </Grid>
          )}

          {ReplaceButton()}
          {ThematicAxisBanner()}
          {Fields()}
          {SubmitButton()}
        </Grid>
        <ReutilizarElaborationDialog
          title="Produtos/Entregas"
          reuseData={data}
          open={reutilizar}
          setOpen={() => setReutilizar(false)}
          setValue={(e) => {
            e.forEach((item: any, index: any) => {
              setValueProductsDeliveries(`produtos_entregas.${index}`, transformProductsDeliveriesDataReuse(item))
            })
          }}
        />
        <DisclaimerReuseElaborationDialog open={openDisclaimer} setOpen={setOpenDisclaimer} />
        <CreateUnityElaboration onClose={() => setOpenUnity(false)} open={openUnity} save={(e) => handleSaveUnity(e)} />
      </Container>
    </form>
  )
}
